/**
 * MasterDataVendorFormDialog — Create/Edit `cp_vn` (Access frmMdQryVendorAdd).
 * Compact Sales Contract style — mirror MasterDataHolidayFormDialog.
 *
 * Layout 2 cột theo toạ độ Access (Top/Left):
 *   code(L) → name(full) → address(full) → phone|short_name → fax|mobile →
 *   email|vat_code → rep|position → status|issued_by(2 dòng) → id_no →
 *   id_place|issuing_date → id_date|offshore_onshore → reg_no|type → note.
 * Edit: code.Enabled=False (verbatim btnEdit_Click main form).
 */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useCreateMdCounterPartyMutation, useUpdateMdCounterPartyMutation } from '@/store/api/masterDataCrudApi'
import type { MasterDataCounterParty, CounterPartyType } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataCounterParty | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  code: string
  name: string
  address: string
  phone: string
  short_name: string
  fax: string
  mobile: string
  email: string
  vat_code: string
  rep: string
  position: string
  status: string
  issued_by: string
  id_no: string
  id_place: string
  issuing_date: string
  id_date: string
  sale_offshore_onshore: string
  reg_no: string
  type_cp: string
  note: string
}

const EMPTY: FormState = {
  code: '', name: '', address: '', phone: '', short_name: '', fax: '', mobile: '',
  email: '', vat_code: '', rep: '', position: '', status: '', issued_by: '',
  id_no: '', id_place: '', issuing_date: '', id_date: '', sale_offshore_onshore: '',
  reg_no: '', type_cp: '', note: '',
}

// Access ComboBox `sale_offshore_onshore` — Value List "Offshore";"Onshore", LimitToList=False.
const OFFSHORE_ONSHORE_OPTIONS = ['Offshore', 'Onshore']
// Access ComboBox `type_cp` — Value List "Customer";"Supplier", LimitToList=True.
const TYPE_OPTIONS = ['Customer', 'Supplier']

export default function MasterDataCounterPartyFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCounterParty, { isLoading: creating }] = useCreateMdCounterPartyMutation()
  const [updateCounterParty, { isLoading: updating }] = useUpdateMdCounterPartyMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        code: initialValue.code ?? '',
        name: initialValue.name ?? '',
        address: initialValue.address ?? '',
        phone: initialValue.phone ?? '',
        short_name: initialValue.short_name ?? '',
        fax: initialValue.fax ?? '',
        mobile: initialValue.mobile ?? '',
        email: initialValue.email ?? '',
        vat_code: initialValue.vat_code ?? '',
        rep: initialValue.rep ?? '',
        position: initialValue.position ?? '',
        status: initialValue.status ?? '',
        issued_by: initialValue.issued_by ?? '',
        id_no: initialValue.id_no ?? '',
        id_place: initialValue.id_place ?? '',
        issuing_date: initialValue.issuing_date ?? '',
        id_date: initialValue.id_date ?? '',
        sale_offshore_onshore: initialValue.sale_offshore_onshore ?? '',
        reg_no: initialValue.reg_no ?? '',
        // Verbatim Access: dialog nhận type_cp hiển thị = IIf([type]=0,"Customer","Supplier").
        type_cp: initialValue.type === 0 ? 'Customer' : 'Supplier',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.code.trim()
  const canSave = !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = (): Partial<MasterDataCounterParty> => {
    const s = (v: string): string | null => {
      const t = v.trim()
      return t ? t : null
    }
    // Verbatim VBA btnSave: cp_type = Customer→0, Supplier→1, else→2.
    const cpType: CounterPartyType = form.type_cp === 'Customer' ? 0 : form.type_cp === 'Supplier' ? 1 : 2
    return {
      name: s(form.name),
      short_name: s(form.short_name),
      address: s(form.address),
      phone: s(form.phone),
      mobile: s(form.mobile),
      fax: s(form.fax),
      vat_code: s(form.vat_code),
      email: s(form.email),
      status: s(form.status),
      rep: s(form.rep),
      position: s(form.position),
      reg_no: s(form.reg_no),
      issued_by: s(form.issued_by),
      issuing_date: form.issuing_date || null,
      id_no: s(form.id_no),
      id_place: s(form.id_place),
      id_date: form.id_date || null,
      note: s(form.note),
      sale_offshore_onshore: s(form.sale_offshore_onshore),
      type: cpType,
    }
  }

  const handleSave = async () => {
    // Verbatim VBA errCheck=1: code rỗng → "Please enter valid Code!".
    if (codeEmpty) {
      setShowErrors(true)
      snack.error('Please enter valid Code!')
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateCounterParty({ code: initialValue.code, body: buildBody() }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createCounterParty({ code: form.code.trim(), ...buildBody() } as MasterDataCounterParty).unwrap()
        snack.success('Successfully Added')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      // Verbatim VBA errCheck=2 (duplicate code khi New).
      if (/already exists/i.test(msg)) {
        snack.warning('Code existed! Please enter a new Code!')
      } else {
        snack.error(msg)
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {/* Access Caption: "Master Data - Vendor" (đổi nhãn → Counter Party). */}
          {isEdit ? 'Edit Counter Party' : 'New Counter Party'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Access row 1: Counter Party code (nửa trái, 1 mình 1 dòng). */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Counter Party Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1 }}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Grid item xs={12}>
            <TextField label="Name" size="small" fullWidth value={form.name}
              onChange={(e) => set('name', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Address" size="small" fullWidth value={form.address}
              onChange={(e) => set('address', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 3 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" size="small" fullWidth value={form.phone}
              onChange={(e) => set('phone', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 4 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Short name" size="small" fullWidth value={form.short_name}
              onChange={(e) => set('short_name', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 5 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="FAX" size="small" fullWidth value={form.fax}
              onChange={(e) => set('fax', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 6 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Mobile" size="small" fullWidth value={form.mobile}
              onChange={(e) => set('mobile', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 7 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" size="small" fullWidth value={form.email}
              onChange={(e) => set('email', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 8 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="VAT code" size="small" fullWidth value={form.vat_code}
              onChange={(e) => set('vat_code', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 9 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="REP" size="small" fullWidth value={form.rep}
              onChange={(e) => set('rep', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 10 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Position" size="small" fullWidth value={form.position}
              onChange={(e) => set('position', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 11 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Status" size="small" fullWidth value={form.status}
              onChange={(e) => set('status', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 12 }} />
          </Grid>
          {/* Access: issued_by cao 2 dòng (Height=792) chiếm luôn ô phải của dòng id_no. */}
          <Grid item xs={12} sm={6}>
            <TextField label="Issued by" size="small" fullWidth value={form.issued_by}
              onChange={(e) => set('issued_by', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 13 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="ID No." size="small" fullWidth value={form.id_no}
              onChange={(e) => set('id_no', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 14 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="ID place" size="small" fullWidth value={form.id_place}
              onChange={(e) => set('id_place', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 15 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Issuing date"
              format="DD-MMM-YYYY"
              value={form.issuing_date ? dayjs(form.issuing_date) : null}
              onChange={(d) => set('issuing_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true }, inputProps: { tabIndex: 16 } } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="ID date"
              format="DD-MMM-YYYY"
              value={form.id_date ? dayjs(form.id_date) : null}
              onChange={(d) => set('id_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true }, inputProps: { tabIndex: 17 } } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              fullWidth
              freeSolo
              options={OFFSHORE_ONSHORE_OPTIONS}
              value={form.sale_offshore_onshore || null}
              inputValue={form.sale_offshore_onshore}
              onChange={(_e, v) => set('sale_offshore_onshore', v ?? '')}
              onInputChange={(_e, v) => set('sale_offshore_onshore', v)}
              renderInput={(params) => (
                <TextField {...params} label="Offshore/Onshore" InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 18 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Reg No." size="small" fullWidth value={form.reg_no}
              onChange={(e) => set('reg_no', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 19 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={TYPE_OPTIONS}
              value={form.type_cp || null}
              onChange={(_e, v) => set('type_cp', v ?? '')}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => (
                <TextField {...params} label="Type" InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 20 }} />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Note" size="small" fullWidth multiline minRows={2} value={form.note}
              onChange={(e) => set('note', e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ tabIndex: 21 }} />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
